import React, { useEffect, useState } from 'react';

const CONSENT_KEY = 'cm_cookie_consent';   // 'accepted' | 'refused'
const META_PIXEL_ID = '1973379576630354';

/**
 * Dynamically injects the Meta Pixel base script and fires PageView.
 * Idempotent: safe to call multiple times.
 */
function loadMetaPixel() {
  if (typeof window === 'undefined') return;
  if (window.fbq) {
    // Already loaded earlier in this session — just track current page
    window.fbq('track', 'PageView');
    return;
  }
  // Inline Meta Pixel base code
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

const GOOGLE_ADS_ID = 'AW-18184601165';

/**
 * Dynamically injects the Google Ads gtag script and configures the conversion ID.
 * Idempotent: safe to call multiple times.
 */
function loadGtag() {
  if (typeof window === 'undefined') return;
  if (window.gtag && window.__gtagLoaded) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GOOGLE_ADS_ID);
  window.__gtagLoaded = true;
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const choice = localStorage.getItem(CONSENT_KEY);
      return choice !== 'accepted' && choice !== 'refused';
    } catch (_) {
      return true;
    }
  });

  // On mount, if consent was previously accepted, fire trackers.
  useEffect(() => {
    let choice = null;
    try { choice = localStorage.getItem(CONSENT_KEY); } catch (_) { /* ignore */ }
    if (choice === 'accepted') {
      loadMetaPixel();
      loadGtag();
    }
  }, []);

  const accept = () => {
    try { localStorage.setItem(CONSENT_KEY, 'accepted'); } catch (_) { /* ignore */ }
    loadMetaPixel();
    loadGtag();
    setVisible(false);
  };

  const refuse = () => {
    try { localStorage.setItem(CONSENT_KEY, 'refused'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Bandeau de consentement aux cookies"
      className="fixed bottom-0 left-0 right-0 z-[70] bg-gray-900 text-white shadow-2xl ring-1 ring-white/10 animate-in slide-in-from-bottom duration-300"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="cookie-consent-banner"
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <p className="text-sm leading-relaxed flex-1 text-gray-200">
          🍪 Nous utilisons des cookies pour optimiser votre expérience et mesurer l&apos;audience. Acceptez-vous ?
          <a
            href="/mentions-legales"
            className="ml-1 text-[#a8e3c0] hover:text-white underline underline-offset-2 whitespace-nowrap"
          >
            En savoir plus
          </a>
        </p>
        <div className="flex gap-2 shrink-0 self-end sm:self-auto">
          <button
            type="button"
            onClick={refuse}
            className="px-4 sm:px-5 py-2 text-sm font-medium rounded-lg border border-white/30 text-gray-200 hover:bg-white/10 transition-colors"
            data-testid="cookie-refuse"
          >
            Refuser
          </button>
          <button
            type="button"
            onClick={accept}
            className="px-4 sm:px-6 py-2 text-sm font-semibold rounded-lg bg-[#52a675] hover:bg-[#3d8a5c] text-white shadow-md transition-colors"
            data-testid="cookie-accept"
          >
            Tout accepter
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
